import React, { useState, useEffect } from "react";
import logo from "../icons/logo.svg";
import { Link, useLocation } from "react-router-dom";
import { HiMenu, HiX, HiMail, HiArrowRight, HiUser } from "react-icons/hi";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const isActive = (path) => {
    return location.pathname === path;
  };

  const handleLogoClick = (e) => {
    if (location.pathname === '/') {
      e.preventDefault();
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const NavLink = ({ to, children }) => (
    <Link
      to={to}
      className={`px-4 py-2 text-sm relative group ${
        isActive(to) 
          ? "text-white" 
          : "text-neutral-400 hover:text-white"
      }`}
    >
      <span className="relative z-10">{children}</span>
      <div className={`absolute inset-0 rounded-full transition-all duration-300 group-hover:bg-white/5 ${
        isActive(to) ? "bg-white/10" : ""
      }`} />
      <div className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-1 w-1 rounded-full bg-primary-500 transition-all duration-300 
      ${isActive(to) ? "w-6 opacity-100" : "w-0 opacity-0 group-hover:opacity-50 group-hover:w-4"}`} />
    </Link>
  );

  return (
    <>
      <div className={`fixed w-full px-5 py-4 z-50 ${
        scrolled ? "bg-black/80 backdrop-blur-xl border-b border-white/5" : "bg-transparent"
      }`}>
        <div className="flex items-center justify-between w-full max-w-screen-xl mx-auto relative">
          <Link 
            to="/" 
            className="flex items-center justify-center z-20 w-32 transition-transform duration-300 hover:scale-105"
            onClick={handleLogoClick}
          >
            <img src={logo} alt="logo" className="h-7" />
          </Link>
          
          <nav className="hidden lg:flex gap-2 justify-center bg-white/5 backdrop-blur-lg px-2 py-1 rounded-full">
            <NavLink to="/case-studies">Case Studies</NavLink>
            <NavLink to="/tools">Tools</NavLink>
            <NavLink to="/about">About</NavLink>
            <NavLink to="/marketplace">Marketplace</NavLink>
            <NavLink to="/contact">Contact</NavLink>
          </nav>

          <div className="hidden lg:flex items-center gap-4 z-20">
            <Link
              to="/get-quote"
              className="group flex items-center whitespace-nowrap gap-2 px-6 py-2.5 text-sm text-white 
              relative overflow-hidden rounded-full transition-all duration-300
              hover:shadow-[0_0_20px_rgba(59,130,246,0.3)]"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-primary-600 transition-transform duration-300 group-hover:scale-105" />
              <span className="relative flex items-center gap-2">
                <HiMail className="w-4 h-4" />
                Get Quote
                <HiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </div>

          <button
            onClick={() => setIsOpen(!isOpen)}
            className="lg:hidden z-20 relative group"
          >
            <div className="p-2 rounded-full transition-colors duration-300 group-hover:bg-white/10">
              {isOpen ? <HiX size={24} className="text-white" /> : <HiMenu size={24} className="text-white" />}
            </div>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`fixed top-0 right-0 h-full w-full sm:w-[min(100vw,24rem)] bg-black/95 backdrop-blur-xl transform 
        transition-all duration-500 flex flex-col justify-between ease-out z-40 ${
          isOpen 
            ? "translate-x-0 opacity-100" 
            : "translate-x-full opacity-0"
        }`}
      >
        <div className="flex flex-col pt-24 px-6 gap-2">
          {[
            { path: "/", label: "Home" },
            { path: "/case-studies", label: "Case Studies" },
            { path: "/about", label: "About" },
            { path: "/marketplace", label: "Marketplace" },
            { path: "/community", label: "Community" },
            { path: "/tools", label: "Tools" },
            { path: "/contact", label: "Contact" },
          ].map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`px-4 py-3 text-sm rounded-xl transition-all duration-300 relative group ${
                isActive(item.path)
                  ? "text-white bg-white/10"
                  : "text-neutral-400 hover:text-white hover:bg-white/5"
              }`}
              onClick={() => setIsOpen(false)}
            >
              <span className="relative z-10">{item.label}</span>
              {isActive(item.path) && (
                <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 bg-primary-500 rounded-full" />
              )}
            </Link>
          ))}
          
          <div className="mt-4">
            <Link
              to="/get-quote"
              className="group flex items-center gap-2 px-6 py-3 text-sm text-white relative overflow-hidden rounded-xl
              transition-all duration-300"
              onClick={() => setIsOpen(false)}
            >
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-blue-500 transition-transform duration-300 group-hover:scale-105" />
              <span className="relative flex items-center gap-2">
                <HiMail className="w-4 h-4" />
                Get Quote
                <HiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
        <div className="px-6 py-4">
            <Link
              to="/members"
              className="group flex items-center justify-between gap-2 px-6 py-3 text-sm text-white relative overflow-hidden rounded-xl border border-blue-600/20 bg-blue-600/20 
              transition-all duration-300"
              onClick={() => setIsOpen(false)}
            >
              <div className="absolute inset-0 transition-transform duration-300 group-hover:scale-105" />
              <span className="relative flex items-center gap-2">
                <HiUser className="w-4 h-4" />
                Members Terminal
              </span>
                <HiArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
      </div>

      {/* Mobile Menu Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-30 lg:hidden 
          transition-opacity duration-500"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default Navbar;
